import { useEffect, useRef } from "react";

// useFocusTrap locks Tab focus inside the given container while the
// modal is mounted. Restores focus to the previously-focused element
// on unmount. Pass { active: false } to temporarily disable (e.g.
// nested modals).
//
// Usage:
//   const ref = useFocusTrap<HTMLDivElement>();
//   return <div ref={ref} role="dialog" aria-modal="true">...</div>;

interface Options {
  active?: boolean;
  // Custom selector for focusable children. Default covers the common
  // interactive elements.
  focusableSelector?: string;
  // Called when Escape is pressed while focus is inside the container
  // (WCAG 2.1.2 / dialog pattern: a modal must be dismissible by keyboard).
  onEscape?: () => void;
}

const DEFAULT_FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useFocusTrap<T extends HTMLElement>(
  options: Options = {}
): React.RefObject<T | null> {
  const { active = true, focusableSelector = DEFAULT_FOCUSABLE } = options;
  const ref = useRef<T | null>(null);
  // Held in a ref so an inline `() => onClose()` doesn't re-run the effect
  // (which would re-steal focus on every render).
  const onEscapeRef = useRef(options.onEscape);
  onEscapeRef.current = options.onEscape;

  useEffect(() => {
    if (!active || !ref.current) return;
    const container = ref.current;

    // Remember the element that was focused before the modal opened so
    // we can restore focus on unmount. Common pattern — screen readers
    // expect focus to return to the button that opened the dialog.
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Focus the first focusable child on mount. If nothing is
    // focusable, focus the container itself.
    const focusables = container.querySelectorAll<HTMLElement>(focusableSelector);
    if (focusables.length > 0) {
      focusables[0].focus();
    } else {
      container.tabIndex = -1;
      container.focus();
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && onEscapeRef.current) {
        e.stopPropagation();
        onEscapeRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const nodes = container.querySelectorAll<HTMLElement>(focusableSelector);
      if (nodes.length === 0) {
        e.preventDefault();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const activeEl = document.activeElement as HTMLElement | null;

      // Shift-Tab on first → loop to last; Tab on last → loop to first
      if (e.shiftKey && activeEl === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && activeEl === last) {
        e.preventDefault();
        first.focus();
      }
    };

    container.addEventListener("keydown", handleKeyDown);
    return () => {
      container.removeEventListener("keydown", handleKeyDown);
      if (previouslyFocused && document.body.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [active, focusableSelector]);

  return ref;
}
